import api from './api';

/**
 * Interfejs reprezentujący materiał
 */
export interface Material {
  id?: number;
  material_id: string;
  project_id: string;
  date: string;
  symbol: string;
  location: string;
  quantity: number;
  unit?: string; // Jednostka miary (j.m.)
  unit_price: number;
  value: number;
  email?: string;
  contact?: string;
  client?: string; // Nowa kolumna Kontrahent
  comments?: string; // Nowa kolumna Uwagi
  is_selected?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Serwis do obsługi operacji na materiałach
 */
const materialService = {
  /**
   * Pobiera wszystkie materiały z paginacją
   * @param {number} page - Numer strony (domyślnie 1)
   * @param {number} limit - Liczba rekordów na stronę (domyślnie 100)
   * @returns {Promise<{materials: Material[], pagination: {total: number, totalPages: number, currentPage: number, limit: number}}>} - Lista materiałów z informacją o paginacji
   */
  getAllMaterials: async (page: number = 1, limit: number = 100): Promise<{materials: Material[], pagination: {total: number, totalPages: number, currentPage: number, limit: number}}> => {
    try {
      const response = await api.get('/materials', {
        params: { page, limit }
      });
      console.log('API Response:', response.data);
      
      // Sprawdź czy odpowiedź ma nowy format z paginacją
      if (response.data && response.data.materials && response.data.pagination) {
        return {
          materials: response.data.materials.map((material: any) => ({
            ...material,
            quantity: parseFloat(material.quantity || 0),
            unit_price: parseFloat(material.unit_price || 0),
            value: parseFloat(material.value || 0),
          })),
          pagination: response.data.pagination
        };
      }
      
      // Fallback dla starego formatu (bez paginacji) - jeśli backend jeszcze nie został zaktualizowany
      if (Array.isArray(response.data)) {
        return {
          materials: response.data.map((material: any) => ({
            ...material,
            quantity: parseFloat(material.quantity || 0),
            unit_price: parseFloat(material.unit_price || 0),
            value: parseFloat(material.value || 0),
          })),
          pagination: {
            total: response.data.length,
            totalPages: Math.ceil(response.data.length / limit),
            currentPage: page,
            limit: limit
          }
        };
      }
      
      throw new Error('Nieprawidłowy format odpowiedzi z API');
    } catch (error: any) {
      console.error('Błąd podczas pobierania materiałów:', error);
      console.error('Szczegóły:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Pobiera materiały dla konkretnego projektu
   * @param {string} projectId - ID projektu
   * @returns {Promise<Material[]>} - Lista materiałów dla projektu
   */
  getMaterialsByProject: async (projectId: string): Promise<Material[]> => {
    try {
      const response = await api.get(`/materials/project/${projectId}`);
      return response.data.map((material: any) => ({
        ...material,
        quantity: parseFloat(material.quantity),
        unit_price: parseFloat(material.unit_price),
        value: parseFloat(material.value),
      }));
    } catch (error) {
      console.error(`Błąd podczas pobierania materiałów dla projektu ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Pobiera materiał po ID
   * @param {number} id - ID materiału
   * @returns {Promise<Material>} - Materiał
   */
  getMaterialById: async (id: number): Promise<Material> => {
    try {
      const response = await api.get(`/materials/${id}`);
      const material = response.data;
      return {
        ...material,
        quantity: parseFloat(material.quantity),
        unit_price: parseFloat(material.unit_price),
        value: parseFloat(material.value),
      };
    } catch (error) {
      console.error(`Błąd podczas pobierania materiału o ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Tworzy nowy materiał
   * @param {Material} material - Dane materiału
   * @returns {Promise<Material>} - Utworzony materiał
   */
  createMaterial: async (material: Material): Promise<Material> => {
    try {
      const response = await api.post('/materials', material);
      const newMaterial = response.data;
      return {
        ...newMaterial,
        quantity: parseFloat(newMaterial.quantity),
        unit_price: parseFloat(newMaterial.unit_price),
        value: parseFloat(newMaterial.value),
      };
    } catch (error) {
      console.error('Błąd podczas tworzenia materiału:', error);
      throw error;
    }
  },

  /**
   * Aktualizuje istniejący materiał
   * @param {number} id - ID materiału
   * @param {Partial<Material>} material - Dane materiału do aktualizacji
   * @returns {Promise<Material>} - Zaktualizowany materiał
   */
  updateMaterial: async (id: number, material: Partial<Material>): Promise<Material> => {
    try {
      const response = await api.put(`/materials/${id}`, material);
      const updatedMaterial = response.data;
      return {
        ...updatedMaterial,
        quantity: parseFloat(updatedMaterial.quantity),
        unit_price: parseFloat(updatedMaterial.unit_price),
        value: parseFloat(updatedMaterial.value),
      };
    } catch (error) {
      console.error(`Błąd podczas aktualizacji materiału o ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Usuwa materiał
   * @param {number} id - ID materiału
   * @returns {Promise<void>}
   */
  deleteMaterial: async (id: number): Promise<void> => {
    try {
      await api.delete(`/materials/${id}`);
    } catch (error) {
      console.error(`Błąd podczas usuwania materiału o ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Aktualizuje pole is_selected dla materiału
   * @param {number} id - ID materiału
   * @param {boolean} isSelected - Nowa wartość pola is_selected
   * @returns {Promise<Material>} - Zaktualizowany materiał
   */
  toggleMaterialSelection: async (id: number, isSelected: boolean): Promise<Material> => {
    try {
      const response = await api.patch(`/materials/${id}/selection`, { is_selected: isSelected });
      const updatedMaterial = response.data;
      return {
        ...updatedMaterial,
        quantity: parseFloat(updatedMaterial.quantity),
        unit_price: parseFloat(updatedMaterial.unit_price),
        value: parseFloat(updatedMaterial.value),
      };
    } catch (error) {
      console.error(`Błąd podczas aktualizacji zaznaczenia materiału o ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Importuje materiały z MSSQL dla danego projektu
   * @param {string} projectId - ID projektu
   * @returns {Promise<{message: string, materials: Material[]}>} - Informacja o zaimportowanych materiałach
   */
  importMaterialsFromMssql: async (projectId: string): Promise<{message: string, materials: Material[]}> => {
    try {
      const response = await api.post(`/materials/import/mssql/${projectId}`);
      const result = response.data;
      return {
        ...result,
        materials: result.materials.map((material: any) => ({
          ...material,
          quantity: parseFloat(material.quantity),
          unit_price: parseFloat(material.unit_price),
          value: parseFloat(material.value),
        })),
      };
    } catch (error) {
      console.error(`Błąd podczas importowania materiałów z MSSQL dla projektu ${projectId}:`, error);
      throw error;
    }
  }
};

export default materialService;
